import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { 
  Heart, User, Crown, Settings2, Star, TrendingUp, 
  ChevronRight, Target, BarChart3, Lock, Loader2, Home
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserFavoritesSync } from "@/hooks/useUserFavoritesSync";
import { useAllTodaysGames, type TransformedGame } from "@/hooks/useLiveGames";
import { useFavoritePlayerStats, type LivePlayerStats } from "@/hooks/useFavoritePlayerStats";
import { formatInTimeZone } from "date-fns-tz";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getNationByCode } from "@/data/hockeyNations";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FavoriteTeamsModal from "@/components/FavoriteTeamsModal";
import FavoritePlayersModal from "@/components/FavoritePlayersModal";
import FavoriteNationSelector from "@/components/FavoriteNationSelector";
import PremiumPlayerStatsModal from "@/components/PremiumPlayerStatsModal";
import SubscriptionModal from "@/components/SubscriptionModal";
import UpgradeNationModal from "@/components/UpgradeNationModal";
import SetNationPromptModal from "@/components/SetNationPromptModal";
import NationOnboardingModal from "@/components/NationOnboardingModal";
import NationHeroSection from "@/components/dashboard/NationHeroSection";
import UpcomingEventsSpotlight from "@/components/dashboard/UpcomingEventsSpotlight";
import DemoDashboard from "@/components/dashboard/DemoDashboard";
import FavoritesOnboardingModal from "@/components/dashboard/FavoritesOnboardingModal";
import SectionSidebar from "@/components/SectionSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { FavoriteTeam, FavoritePlayer } from "@/hooks/useUserFavorites";

const LEAGUE_FLAGS: Record<string, string> = {
  NHL: "🇺🇸", AHL: "🇺🇸", ECHL: "🇺🇸", KHL: "🇷🇺", SHL: "🇸🇪",
  Allsvenskan: "🇸🇪", Liiga: "🇫🇮", Extraliga: "🇨🇿", DEL: "🇩🇪", NL: "🇨🇭",
};

const NATIONALITY_FLAGS: Record<string, string> = {
  USA: "🇺🇸", CAN: "🇨🇦", SWE: "🇸🇪", FIN: "🇫🇮", RUS: "🇷🇺",
  CZE: "🇨🇿", GER: "🇩🇪", SUI: "🇨🇭", SVK: "🇸🇰", LAT: "🇱🇻",
};

// Map ISO codes to country names used in Olympics schedule
const getNationName = (isoCode: string): string | null => {
  const mapping: Record<string, string> = {
    CAN: "Canada",
    USA: "United States",
    SWE: "Sweden",
    FIN: "Finland",
    RUS: "Russia",
    CZE: "Czechia",
    CHE: "Switzerland",
    DEU: "Germany",
    SVK: "Slovakia",
    LVA: "Latvia",
    DNK: "Denmark",
    NOR: "Norway",
    FRA: "France",
    AUT: "Austria",
    KAZ: "Kazakhstan",
    JPN: "Japan",
  };
  return mapping[isoCode] || null;
};

// Team Game Card
const TeamGameCard = ({ game }: { game: TransformedGame }) => {
  const localTime = useMemo(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return formatInTimeZone(new Date(game.matchStartUTC), tz, "h:mm a");
  }, [game.matchStartUTC]);

  return (
    <div className="bg-secondary/50 rounded-xl p-4 border border-border hover:border-primary/30 transition-all">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{LEAGUE_FLAGS[game.league] || "🏒"}</span>
          <span className="text-xs font-medium text-muted-foreground">{game.league}</span>
        </div>
        {game.isLive ? (
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-score-live rounded-full animate-pulse" />
            <span className="text-xs font-semibold text-score-live uppercase">Live</span>
          </div>
        ) : (
          <span className="text-xs text-primary font-medium">{localTime}</span>
        )}
      </div>
      <div className="space-y-2">
        {[{ team: game.homeTeam, abbr: game.homeAbbr, logo: game.homeLogo, score: game.homeScore },
          { team: game.awayTeam, abbr: game.awayAbbr, logo: game.awayLogo, score: game.awayScore }]
          .map((t, i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {t.logo ? (
                <img src={t.logo} alt={t.team} className="w-6 h-6 object-contain" />
              ) : (
                <div className="w-6 h-6 rounded bg-secondary flex items-center justify-center text-[10px] font-bold">{t.abbr}</div>
              )}
              <span className="text-sm font-medium text-foreground">{t.team}</span>
            </div>
            <span className={`font-display text-xl font-bold ${game.isLive ? 'text-foreground' : 'text-muted-foreground'}`}>
              {game.isLive ? t.score : '-'}
            </span>
          </div>
        ))}
      </div>
      {game.isLive && (
        <div className="mt-3 pt-3 border-t border-border/50">
          <span className="text-xs text-muted-foreground">{game.period} • {game.time}</span>
        </div>
      )}
    </div>
  );
};

// Player Card
interface PlayerCardProps {
  player: FavoritePlayer;
  stats?: LivePlayerStats | null;
  isLoading?: boolean;
  hasPremiumAccess: boolean;
  onViewDetails: () => void;
}

const DashboardPlayerCard = ({ player, stats, isLoading, hasPremiumAccess, onViewDetails }: PlayerCardProps) => {
  const flag = player.nationality ? NATIONALITY_FLAGS[player.nationality] || "" : "";
  const isGoalie = player.position === "G" || stats?.isGoalie;

  return (
    <div className="bg-secondary/50 rounded-xl p-4 border border-border hover:border-primary/30 transition-all group">
      <div className="flex items-start gap-3">
        {player.photo ? (
          <img src={player.photo} alt={player.name} className="w-12 h-12 rounded-full object-cover bg-secondary ring-2 ring-border" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-sm font-bold text-muted-foreground ring-2 ring-border">
            {player.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            {flag && <span className="text-sm">{flag}</span>}
            <h3 className="font-medium text-foreground truncate text-sm">{player.name}</h3>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="px-1.5 py-0.5 bg-primary/10 text-primary rounded font-medium text-[10px]">{player.position}</span>
            <span>{player.team}</span>
            <span className="text-muted-foreground/50">·</span>
            <span>{player.league}</span>
          </div>
        </div>
      </div>

      {stats ? (
        <div className="mt-3 pt-3 border-t border-border/50">
          {isGoalie ? (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><p className="text-lg font-display font-bold text-foreground">{stats.wins || 0}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Wins</p></div>
              <div><p className="text-lg font-display font-bold text-foreground">{stats.gaa?.toFixed(2) || "0.00"}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">GAA</p></div>
              <div><p className="text-lg font-display font-bold text-foreground">{stats.svPct ? (stats.svPct * 100).toFixed(1) + "%" : "0%"}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">SV%</p></div>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-1 text-center">
              <div><p className="text-lg font-display font-bold text-foreground">{stats.goals}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">G</p></div>
              <div><p className="text-lg font-display font-bold text-foreground">{stats.assists}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">A</p></div>
              <div><p className="text-lg font-display font-bold text-primary">{stats.points}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">PTS</p></div>
              <div><p className={`text-lg font-display font-bold ${(stats.plusMinus || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>{(stats.plusMinus || 0) >= 0 ? '+' : ''}{stats.plusMinus || 0}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">+/-</p></div>
            </div>
          )}
          <div className="mt-2 flex items-center justify-center gap-1 text-[10px] text-muted-foreground">
            <Target className="w-3 h-3" /><span>{stats.gp} GP</span>
          </div>
        </div>
      ) : isLoading ? (
        <div className="mt-3 pt-3 border-t border-border/50 grid grid-cols-4 gap-1">
          {[1,2,3,4].map(i => <div key={i} className="flex flex-col items-center gap-1"><Skeleton className="h-5 w-8" /><Skeleton className="h-2 w-4" /></div>)}
        </div>
      ) : (
        <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-center gap-2 text-xs text-muted-foreground py-2">
          <TrendingUp className="w-3 h-3" /><span>Stats loading...</span>
        </div>
      )}

      <button
        onClick={onViewDetails}
        className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg transition-all bg-secondary/80 text-muted-foreground hover:bg-primary/10 hover:text-primary"
      >
        {hasPremiumAccess ? (
          <><BarChart3 className="w-3.5 h-3.5" />View Full Stats</>
        ) : (
          <><Crown className="w-3.5 h-3.5 text-amber-400" /><span className="text-amber-400">Premium Stats</span></>
        )}
      </button>
    </div>
  );
};

// Onboarding Card
const OnboardingCard = ({ type, onAction, canEdit, onUpgrade }: { 
  type: "teams" | "players"; 
  onAction: () => void; 
  canEdit: boolean;
  onUpgrade: () => void;
}) => {
  const isTeams = type === "teams";
  const Icon = isTeams ? Heart : User;

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardContent className="pt-6 text-center">
        <Icon className="w-12 h-12 text-primary mx-auto mb-4" />
        <h3 className="font-display text-xl font-bold text-foreground mb-2">
          {isTeams ? "Choose Your Teams" : "Follow Your Players"}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {isTeams 
            ? "Select your favorite leagues and teams to see their games here."
            : "Track players from NHL, KHL, SHL, and more to see their stats."
          }
        </p>
        {canEdit ? (
          <Button onClick={onAction} className="gap-2">
            <Star className="w-4 h-4" />
            {isTeams ? "Choose Teams" : "Choose Players"}
          </Button>
        ) : (
          <Button onClick={onUpgrade} variant="outline" className="gap-2 border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
            <Crown className="w-4 h-4" />
            Upgrade to Customize
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Upgrade Banner
const UpgradeBanner = ({ onUpgrade }: { onUpgrade: () => void }) => (
  <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border border-amber-500/20">
    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className="p-3 bg-amber-500/20 rounded-xl">
          <Crown className="w-8 h-8 text-amber-400" />
        </div>
        <div>
          <h3 className="font-display text-lg font-bold text-foreground">Unlock Full Customization</h3>
          <p className="text-sm text-muted-foreground">Upgrade to Pro or Elite for priority alerts, detailed stats, and full personalization.</p>
        </div>
      </div>
      <Button onClick={onUpgrade} className="gap-2 bg-amber-500 hover:bg-amber-600 text-black font-semibold">
        <Crown className="w-4 h-4" />
        Upgrade Now
      </Button>
    </div>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isLoading: authLoading, subscription, checkSubscription } = useAuth();
  const { dbTeams, dbPlayers, isLoading: favoritesLoading } = useUserFavoritesSync();
  const { data: games } = useAllTodaysGames();
  const { data: playerStatsData, isLoading: statsLoading } = useFavoritePlayerStats(dbPlayers);

  const [showTeamsModal, setShowTeamsModal] = useState(false);
  const [showPlayersModal, setShowPlayersModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showNationModal, setShowNationModal] = useState(false);
  const [showUpgradeNationModal, setShowUpgradeNationModal] = useState(false);
  const [showSetNationPrompt, setShowSetNationPrompt] = useState(false);
  const [showNationOnboarding, setShowNationOnboarding] = useState(false);
  const [showFavoritesOnboarding, setShowFavoritesOnboarding] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<FavoritePlayer | null>(null);
  
  // Track if we just upgraded (to show nation prompt)
  const wasNotSubscribed = useRef(!subscription.subscribed);
  // Track if onboarding was checked this session
  const onboardingChecked = useRef(false);

  // Fetch user's favorite nation
  const { data: userProfile, refetch: refetchProfile } = useQuery({
    queryKey: ["userProfile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("favorite_nation")
        .eq("user_id", user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const favoriteNation = getNationByCode(userProfile?.favorite_nation ?? null);
  const favoriteNationName = userProfile?.favorite_nation 
    ? getNationName(userProfile.favorite_nation) 
    : null;

  // Tier checks
  const canEdit = subscription.subscribed && (subscription.tier === "pro" || subscription.tier === "elite");
  const hasPremiumAccess = canEdit;
  const isFreeUser = !subscription.subscribed || subscription.tier === null;
  const hasNation = !!favoriteNation && !!favoriteNationName;

  // Handle checkout success - refresh subscription status and prompt nation selection
  useEffect(() => {
    const checkout = searchParams.get("checkout");
    if (checkout === "success") {
      toast.success("🎉 Subscription activated! Welcome to the premium experience.");
      // Refresh subscription status
      checkSubscription();
      // Clear the URL parameter
      setSearchParams({}, { replace: true });
      // Show nation prompt if user doesn't have one set
      if (!hasNation) {
        setTimeout(() => setShowSetNationPrompt(true), 500);
      }
    }
  }, [searchParams, setSearchParams, checkSubscription, hasNation]);

  // Also detect subscription upgrade in-session (without checkout param)
  useEffect(() => {
    if (wasNotSubscribed.current && canEdit && !hasNation) {
      wasNotSubscribed.current = false;
      setTimeout(() => setShowSetNationPrompt(true), 500);
    }
    wasNotSubscribed.current = !subscription.subscribed;
  }, [subscription.subscribed, canEdit, hasNation]);

  // Show favorites onboarding for logged-in Pro/Elite users with no favorites
  useEffect(() => {
    if (authLoading || favoritesLoading) return;
    if (!user || !canEdit) return;
    if (onboardingChecked.current) return;
    
    const hasAny = dbTeams.length > 0 || dbPlayers.length > 0;
    const skippedFav = localStorage.getItem("favorites_onboarding_skipped");
    const skippedNation = localStorage.getItem("nation_onboarding_skipped");
    if (!hasAny && !hasNation && !skippedFav && !skippedNation) {
      onboardingChecked.current = true;
      setTimeout(() => setShowFavoritesOnboarding(true), 800);
    }
  }, [authLoading, favoritesLoading, user, canEdit, hasNation, dbTeams.length, dbPlayers.length]);

  // Nation-only onboarding (kept for users who already set teams/players but not nation)
  useEffect(() => {
    if (onboardingChecked.current) return;
    if (authLoading || favoritesLoading) return;
    if (!user || !canEdit) return;
    
    const hasAny = dbTeams.length > 0 || dbPlayers.length > 0;
    if (!hasAny) return; // FavoritesOnboardingModal handles this case
    
    const skipped = localStorage.getItem("nation_onboarding_skipped");
    if (!hasNation && !skipped) {
      onboardingChecked.current = true;
      setTimeout(() => setShowNationOnboarding(true), 800);
    }
  }, [authLoading, favoritesLoading, user, canEdit, hasNation, dbTeams.length, dbPlayers.length]);

  // Filter games for user's teams
  const favoriteGames = useMemo(() => {
    if (!games || dbTeams.length === 0) return [];
    const teamKeys = new Set(dbTeams.map(t => `${t.league}:${t.abbr}`));
    return games.filter(game => 
      teamKeys.has(`${game.league}:${game.homeAbbr}`) || teamKeys.has(`${game.league}:${game.awayAbbr}`)
    ).sort((a, b) => {
      if (a.isLive && !b.isLive) return -1;
      if (!a.isLive && b.isLive) return 1;
      return new Date(a.matchStartUTC).getTime() - new Date(b.matchStartUTC).getTime();
    });
  }, [games, dbTeams]);

  // Player stats map
  const playerStatsMap = useMemo(() => {
    const map = new Map<string, LivePlayerStats | null>();
    if (playerStatsData) {
      playerStatsData.forEach((stats, key) => map.set(key, stats));
    }
    return map;
  }, [playerStatsData]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background pt-16">
        <Navbar />
        <main className="container mx-auto px-6 py-12">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  // Show demo dashboard for non-authenticated users
  if (!user) {
    return (
      <div className="min-h-screen bg-background pt-16">
        <Navbar />
        <SectionSidebar />
        <main className="container mx-auto px-6 py-8">
          <div className="mb-6">
            <Link to="/#hero" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <Home className="w-4 h-4" />Back to Home
            </Link>
          </div>
          <div className="mb-8">
            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">My Page – Demo 🏒</h1>
            <p className="text-muted-foreground">See what a personalized dashboard looks like.</p>
          </div>
          <DemoDashboard 
            onSignIn={() => setShowSubscriptionModal(true)} 
            onUpgrade={() => setShowSubscriptionModal(true)}
            isLoggedIn={false}
          />
        </main>
        <Footer />
        <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} onNeedAuth={() => {}} />
      </div>
    );
  }

  if (favoritesLoading) {
    return (
      <div className="min-h-screen bg-background pt-16">
        <Navbar />
        <main className="container mx-auto px-6 py-12">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  const hasTeams = dbTeams.length > 0;
  const hasPlayers = dbPlayers.length > 0;
  const hasAnyFavorites = hasTeams || hasPlayers;

  return (
    <div className="min-h-screen bg-background pt-16">
      <Navbar />
      <SectionSidebar />
      <main className="container mx-auto px-6 py-8">
        {/* Back to Home button */}
        <div className="mb-6">
          <Link 
            to="/#hero" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Home className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">
            {hasNation && canEdit 
              ? `${favoriteNation!.flag} Welcome back!` 
              : "Welcome back! 🏒"
            }
          </h1>
          <p className="text-muted-foreground mb-4">
            {hasNation 
              ? `Your personalized hockey experience for ${favoriteNation!.name}.`
              : "Your personalized hockey dashboard with teams and players you follow."
            }
          </p>
          <Link 
            to="/#hero" 
            className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg bg-secondary hover:bg-secondary/80 text-foreground transition-colors"
          >
            <Home className="w-4 h-4" />
            Explore Homepage
          </Link>
        </div>

        {/* Free-user: show demo dashboard with upgrade CTA */}
        {isFreeUser && !hasAnyFavorites ? (
          <DemoDashboard 
            onSignIn={() => setShowSubscriptionModal(true)}
            onUpgrade={() => setShowSubscriptionModal(true)}
            isLoggedIn={true}
          />
        ) : (
          <>
            {/* Nation Hero Section OR Olympics Spotlight */}
            {hasNation && canEdit && favoriteNationName ? (
              <NationHeroSection
                nation={favoriteNation!}
                nationName={favoriteNationName}
                onEditNation={() => setShowNationModal(true)}
                canEdit={canEdit}
              />
            ) : (
              <UpcomingEventsSpotlight 
                showUpgradePrompt={isFreeUser || !hasNation}
                onUpgrade={() => canEdit ? setShowNationModal(true) : setShowUpgradeNationModal(true)}
              />
            )}

        {/* Your Teams Section */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Heart className="w-5 h-5 text-primary fill-primary" />
              </div>
              <div>
                <h2 className="font-display text-2xl font-bold text-foreground">Your Teams</h2>
                {hasTeams && <p className="text-sm text-muted-foreground">{dbTeams.length} team{dbTeams.length !== 1 ? 's' : ''}</p>}
              </div>
            </div>
            {hasTeams && canEdit && (
              <Button variant="outline" size="sm" onClick={() => setShowTeamsModal(true)} className="gap-2">
                <Settings2 className="w-4 h-4" />
                Edit
              </Button>
            )}
            {hasTeams && !canEdit && (
              <Button variant="outline" size="sm" onClick={() => setShowSubscriptionModal(true)} className="gap-2 border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
                <Lock className="w-4 h-4" />
                Upgrade to Edit
              </Button>
            )}
          </div>

          {hasTeams ? (
            favoriteGames.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {favoriteGames.slice(0, 8).map(game => <TeamGameCard key={game.id} game={game} />)}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No games today for your favorite teams</p>
                <p className="text-sm text-muted-foreground/70 mt-1">Check back later or view the full schedule</p>
              </Card>
            )
          ) : (
            <OnboardingCard type="teams" onAction={() => setShowTeamsModal(true)} canEdit={canEdit} onUpgrade={() => setShowSubscriptionModal(true)} />
          )}

          {favoriteGames.length > 8 && (
            <div className="flex justify-center mt-6">
              <button className="flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors">
                View all {favoriteGames.length} games
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </section>

        {/* Your Players Section */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-display text-2xl font-bold text-foreground">Your Players</h2>
                {hasPlayers && <p className="text-sm text-muted-foreground">{dbPlayers.length} player{dbPlayers.length !== 1 ? 's' : ''}</p>}
              </div>
            </div>
            {hasPlayers && canEdit && (
              <Button variant="outline" size="sm" onClick={() => setShowPlayersModal(true)} className="gap-2">
                <Settings2 className="w-4 h-4" />
                Edit
              </Button>
            )}
            {hasPlayers && !canEdit && (
              <Button variant="outline" size="sm" onClick={() => setShowSubscriptionModal(true)} className="gap-2 border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
                <Lock className="w-4 h-4" />
                Upgrade to Edit
              </Button>
            )}
          </div>

          {hasPlayers ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {dbPlayers.slice(0, 8).map(player => (
                <DashboardPlayerCard
                  key={`${player.league}-${player.id}`}
                  player={player}
                  stats={playerStatsMap.get(`${player.league}-${player.id}`)}
                  isLoading={statsLoading}
                  hasPremiumAccess={hasPremiumAccess}
                  onViewDetails={() => {
                    setSelectedPlayer(player);
                    setShowPremiumModal(true);
                  }}
                />
              ))}
            </div>
          ) : (
            <OnboardingCard type="players" onAction={() => setShowPlayersModal(true)} canEdit={canEdit} onUpgrade={() => setShowSubscriptionModal(true)} />
          )}

          {dbPlayers.length > 8 && (
            <div className="flex justify-center mt-6">
              <button onClick={() => setShowPlayersModal(true)} className="flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors">
                View all {dbPlayers.length} players
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </section>

        {/* Quick Links when no favorites */}
        {!hasAnyFavorites && (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              Or explore the homepage to discover teams and players to follow.
            </p>
            <Button variant="outline" onClick={() => navigate("/")}>
              Browse Homepage
            </Button>
          </div>
        )}
          </>
        )}
      </main>
      <Footer />

      {/* Modals */}
      <FavoriteTeamsModal isOpen={showTeamsModal} onClose={() => setShowTeamsModal(false)} />
      <FavoritePlayersModal isOpen={showPlayersModal} onClose={() => setShowPlayersModal(false)} />
      <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} onNeedAuth={() => {}} />
      
      {/* Upgrade Nation Modal - shown when free user tries to edit nation */}
      <UpgradeNationModal 
        isOpen={showUpgradeNationModal} 
        onClose={() => setShowUpgradeNationModal(false)}
        onUpgrade={() => setShowSubscriptionModal(true)}
      />
      
      {/* Set Nation Prompt - shown after successful subscription upgrade */}
      <SetNationPromptModal
        isOpen={showSetNationPrompt}
        onClose={() => setShowSetNationPrompt(false)}
        onSetNation={() => setShowNationModal(true)}
      />
      
      {/* Nation Onboarding Modal - shown on first login for Pro/Elite without nation */}
      <NationOnboardingModal
        isOpen={showNationOnboarding}
        onClose={() => setShowNationOnboarding(false)}
        onSuccess={() => refetchProfile()}
      />
      
      {/* Favorites Onboarding Modal - shown for Pro/Elite with no favorites */}
      <FavoritesOnboardingModal
        isOpen={showFavoritesOnboarding}
        onClose={() => {
          setShowFavoritesOnboarding(false);
          localStorage.setItem("favorites_onboarding_skipped", "true");
        }}
        onSetNation={() => setShowNationOnboarding(true)}
        onSetTeams={() => setShowTeamsModal(true)}
        onSetPlayers={() => setShowPlayersModal(true)}
        hasNation={hasNation}
        hasTeams={hasTeams}
        hasPlayers={hasPlayers}
      />
      
      {/* Nation Selection Modal */}
      <Dialog open={showNationModal} onOpenChange={setShowNationModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Your Favorite Nation</DialogTitle>
          </DialogHeader>
          <FavoriteNationSelector onSave={() => {
            setShowNationModal(false);
            refetchProfile();
          }} />
        </DialogContent>
      </Dialog>
      
      {selectedPlayer && (
        <PremiumPlayerStatsModal
          isOpen={showPremiumModal}
          onClose={() => {
            setShowPremiumModal(false);
            setSelectedPlayer(null);
          }}
          player={selectedPlayer}
        />
      )}
    </div>
  );
}
